import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import VoiceInput from '@/components/VoiceInput';
import { Plus, Undo2, CheckCircle2, Target, ClipboardList, Stethoscope, NotebookPen, ArrowLeft, FileDown, BellOff, ShieldAlert } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_COLORS = { Technical: '#2563eb', Tactical: '#f59e0b', Physical: '#16a34a' };
const PROFILE_CATEGORIES = ['Technical', 'Tactical', 'Physical'];

function goalProgressPct(goal) {
  if (!goal.target_date || goal.status !== 'open') return null;
  const created = new Date(goal.created_at);
  const target = new Date(goal.target_date);
  const total = target - created;
  if (!Number.isFinite(total) || total <= 0) return 100;
  const elapsed = new Date() - created;
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
}

function isGoalOverdue(goal) {
  return goal.status === 'open' && goal.target_date && new Date(goal.target_date) < new Date();
}

function parseVoiceEvaluation(text, categories) {
  const lower = text.toLowerCase();
  let category = null;
  for (const c of categories) {
    if (lower.includes(c.toLowerCase())) { category = c; break; }
  }
  let rating = null;
  const m = lower.match(/(\d{1,2})\s*(?:\/|out of)\s*10/) || lower.match(/rate[^\d]{0,20}(\d{1,2})/);
  if (m) {
    const val = parseInt(m[1], 10);
    if (val >= 1 && val <= 10) rating = val;
  }
  return { category, rating };
}

export default function PlayerDevelopmentPage({ user, onLogout }) {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [categories, setCategories] = useState(['Technical', 'Tactical', 'Physical']);
  const [loading, setLoading] = useState(true);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [showEvalDialog, setShowEvalDialog] = useState(false);
  const [showInterventionDialog, setShowInterventionDialog] = useState(false);
  const [goalForm, setGoalForm] = useState({ category: 'Technical', title: '', description: '', target_date: '' });
  const [evalForm, setEvalForm] = useState({ category: 'Technical', rating: 5, tags: '', notes: '' });
  const [interventionForm, setInterventionForm] = useState({ category: '', title: '', description: '', goal_id: '' });
  const [profileDrafts, setProfileDrafts] = useState({});
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const isCoach = user.role === 'head_coach' || user.role === 'assistant_coach';

  const load = async () => {
    try {
      const [profRes, catRes] = await Promise.all([
        axios.get(`${API}/player-intel/players/${playerId}`, { params: { user_id: user.id, team_id: user.team_id } }),
        axios.get(`${API}/player-intel/teams/${user.team_id}/categories`, { params: { user_id: user.id } }),
      ]);
      setProfile(profRes.data);
      setCategories(catRes.data.categories || categories);
      const drafts = {};
      PROFILE_CATEGORIES.forEach((c) => {
        const key = c.toLowerCase();
        drafts[key] = profRes.data.development_profile?.[key]?.summary || '';
      });
      setProfileDrafts(drafts);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load development profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCoach) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/player-intel/players/${playerId}/goals`,
        { user_id: user.id, category: goalForm.category, title: goalForm.title, description: goalForm.description, target_date: goalForm.target_date || null },
        { params: { team_id: user.team_id } }
      );
      toast.success('Goal added');
      setShowGoalDialog(false);
      setGoalForm({ category: 'Technical', title: '', description: '', target_date: '' });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add goal');
    }
  };

  const handleCloseGoal = async (goalId) => {
    try {
      await axios.post(`${API}/player-intel/players/${playerId}/goals/${goalId}/close`, { user_id: user.id, reason: 'achieved' }, { params: { team_id: user.team_id } });
      toast.success('Goal closed');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close goal');
    }
  };

  const handleUndoGoal = async (goalId) => {
    try {
      await axios.post(`${API}/player-intel/players/${playerId}/goals/${goalId}/undo`, { user_id: user.id }, { params: { team_id: user.team_id } });
      toast.success('Undone');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo');
    }
  };

  const handleSnoozeGoal = async (goalId) => {
    try {
      await axios.post(`${API}/player-intel/players/${playerId}/goals/${goalId}/snooze`, { user_id: user.id }, { params: { team_id: user.team_id } });
      toast.success('Reminders snoozed for 7 days');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to snooze');
    }
  };

  const handleCreateEvaluation = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/player-intel/players/${playerId}/evaluations`,
        {
          user_id: user.id,
          category: evalForm.category,
          rating: parseInt(evalForm.rating, 10),
          tags: evalForm.tags.split(',').map((t) => t.trim()).filter(Boolean),
          notes: evalForm.notes,
        },
        { params: { team_id: user.team_id } }
      );
      toast.success('Evaluation recorded');
      setShowEvalDialog(false);
      setEvalForm({ category: 'Technical', rating: 5, tags: '', notes: '' });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record evaluation');
    }
  };

  const handleUndoEvaluation = async (evalId) => {
    try {
      await axios.post(`${API}/player-intel/players/${playerId}/evaluations/${evalId}/undo`, { user_id: user.id }, { params: { team_id: user.team_id } });
      toast.success('Retracted');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retract');
    }
  };

  const handleEvaluationVoiceTranscript = (text) => {
    const { category, rating } = parseVoiceEvaluation(text, categories);
    setEvalForm((prev) => ({
      ...prev,
      category: category || prev.category,
      rating: rating || prev.rating,
      notes: prev.notes ? `${prev.notes} ${text}` : text,
    }));
    if (category || rating) {
      toast.success(`Picked up${category ? ` category: ${category}` : ''}${rating ? `, rating: ${rating}/10` : ''}`);
    }
  };

  const handleCreateIntervention = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/player-intel/players/${playerId}/interventions`,
        { user_id: user.id, category: interventionForm.category || null, title: interventionForm.title, description: interventionForm.description, goal_id: interventionForm.goal_id || null },
        { params: { team_id: user.team_id } }
      );
      toast.success('Intervention logged');
      setShowInterventionDialog(false);
      setInterventionForm({ category: '', title: '', description: '', goal_id: '' });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log intervention');
    }
  };

  const handleUndoIntervention = async (interventionId) => {
    try {
      await axios.post(`${API}/player-intel/players/${playerId}/interventions/${interventionId}/undo`, { user_id: user.id }, { params: { team_id: user.team_id } });
      toast.success('Retracted');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retract');
    }
  };

  const handleSaveProfile = async (category) => {
    const key = category.toLowerCase();
    try {
      await axios.put(
        `${API}/player-intel/players/${playerId}/profile`,
        { user_id: user.id, category: key, summary: profileDrafts[key] || '' },
        { params: { team_id: user.team_id } }
      );
      toast.success(`${category} notes saved`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notes');
    }
  };

  const handleExportPdf = async () => {
    setIsExportingPdf(true);
    try {
      const res = await axios.get(`${API}/player-intel/players/${playerId}/report.pdf`, {
        params: { user_id: user.id, team_id: user.team_id },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${profile?.player?.last_name || 'player'}_development_profile.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setIsExportingPdf(false);
    }
  };

  if (!isCoach) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-8 flex items-center justify-center min-h-[60vh]" data-testid="player-dev-no-access">
          <Card className="max-w-md">
            <CardContent className="p-12 text-center space-y-4">
              <ShieldAlert className="h-12 w-12 text-destructive mx-auto" data-testid="player-dev-no-access-icon" />
              <h2 className="font-heading text-2xl font-black uppercase tracking-tight text-foreground">
                Access Restricted
              </h2>
              <p className="text-muted-foreground">
                Player Development Profiles are only available to coaches.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                data-testid="player-dev-no-access-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-8 flex justify-center" data-testid="player-dev-loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  if (!profile) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-8" data-testid="player-dev-error">
          <Card><CardContent className="p-12 text-center">Could not load this player's development profile.</CardContent></Card>
        </div>
      </Layout>
    );
  }

  const { player, goals, evaluations, interventions } = profile;
  const openGoals = goals.filter((g) => g.status === 'open');
  const closedGoals = goals.filter((g) => g.status !== 'open');
  const activeEvaluations = evaluations.filter((e) => !e.retracted);
  const trendData = [...activeEvaluations]
    .sort((a, b) => new Date(a.evaluated_at) - new Date(b.evaluated_at))
    .map((ev) => ({ date: new Date(ev.evaluated_at).toLocaleDateString(), [ev.category]: ev.rating }));

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-8" data-testid="player-dev-page">
        <div className="border-b border-border pb-6 flex items-start justify-between">
          <div>
            <button
              onClick={() => navigate('/players')}
              className="flex items-center gap-1 text-xs uppercase text-muted-foreground font-semibold hover:text-primary"
              data-testid="back-to-roster-btn"
            >
              <ArrowLeft className="h-3 w-3" /> Back to Roster
            </button>
            <h1 className="font-heading text-4xl font-black uppercase tracking-tighter text-primary mt-2">
              {player.first_name} {player.last_name} — Development Profile
            </h1>
            <p className="text-muted-foreground mt-1">#{player.jersey_number} · {player.position || 'Position TBD'}</p>
          </div>
          <Button variant="outline" onClick={handleExportPdf} disabled={isExportingPdf} data-testid="export-pdf-btn">
            <FileDown className="h-4 w-4 mr-2" />
            {isExportingPdf ? 'Exporting...' : 'Export PDF'}
          </Button>
        </div>

        <Tabs defaultValue="goals" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="goals" data-testid="dev-tab-goals"><Target className="h-4 w-4 mr-1" />Goals</TabsTrigger>
            <TabsTrigger value="evaluations" data-testid="dev-tab-evaluations"><ClipboardList className="h-4 w-4 mr-1" />Evaluations</TabsTrigger>
            <TabsTrigger value="interventions" data-testid="dev-tab-interventions"><Stethoscope className="h-4 w-4 mr-1" />Interventions</TabsTrigger>
            <TabsTrigger value="profile" data-testid="dev-tab-profile"><NotebookPen className="h-4 w-4 mr-1" />Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="goals" className="space-y-4 mt-4">
            <div className="flex justify-end">
              <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-goal-btn"><Plus className="h-4 w-4 mr-2" />New Goal</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>New Development Goal</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateGoal} className="space-y-4" data-testid="goal-form">
                    <div>
                      <Label>Category</Label>
                      <Select value={goalForm.category} onValueChange={(v) => setGoalForm({ ...goalForm, category: v })}>
                        <SelectTrigger data-testid="goal-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input required value={goalForm.title} onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })} data-testid="goal-title-input" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={goalForm.description} onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })} data-testid="goal-description-input" />
                    </div>
                    <div>
                      <Label>Target Date</Label>
                      <Input type="date" value={goalForm.target_date} onChange={(e) => setGoalForm({ ...goalForm, target_date: e.target.value })} data-testid="goal-target-date-input" />
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-goal-btn">Add Goal</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {goals.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">No goals yet.</CardContent></Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {[...openGoals, ...closedGoals].map((g) => (
                  <Card key={g.id} data-testid={`goal-card-${g.id}`} className={g.status !== 'open' ? 'opacity-60' : ''}>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">{g.category}</Badge>
                        <Badge variant={g.status === 'open' ? 'default' : 'secondary'} data-testid={`goal-status-${g.id}`}>{g.status}</Badge>
                      </div>
                      <p className="font-semibold">{g.title}</p>
                      {g.description && <p className="text-sm text-muted-foreground">{g.description}</p>}
                      {g.target_date && <p className="text-xs text-muted-foreground">Target: {g.target_date}</p>}
                      {g.status === 'open' && g.target_date && (
                        <div className="space-y-1 pt-1" data-testid={`goal-progress-${g.id}`}>
                          <Progress value={goalProgressPct(g)} className="h-1.5" />
                          <p className="text-[11px] text-muted-foreground">
                            {goalProgressPct(g) >= 100 ? 'Overdue' : `${goalProgressPct(g)}% of time elapsed`}
                          </p>
                        </div>
                      )}
                      <div className="flex gap-2 pt-2">
                        {g.status === 'open' && (
                          <Button size="sm" variant="outline" onClick={() => handleCloseGoal(g.id)} data-testid={`close-goal-${g.id}`}>
                            <CheckCircle2 className="h-4 w-4 mr-1" />Close
                          </Button>
                        )}
                        {isGoalOverdue(g) && (
                          <Button size="sm" variant="outline" onClick={() => handleSnoozeGoal(g.id)} data-testid={`snooze-goal-${g.id}`}>
                            <BellOff className="h-4 w-4 mr-1" />Snooze 7d
                          </Button>
                        )}
                        {g.status !== 'cancelled' && (
                          <Button size="sm" variant="ghost" onClick={() => handleUndoGoal(g.id)} data-testid={`undo-goal-${g.id}`}>
                            <Undo2 className="h-4 w-4 mr-1" />Undo
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="evaluations" className="space-y-4 mt-4">
            {activeEvaluations.length > 1 && (
              <Card data-testid="evaluation-trend-chart">
                <CardHeader><CardTitle className="text-lg">Rating Trend</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      {categories.map((c) => (
                        <Line key={c} type="monotone" dataKey={c} stroke={CATEGORY_COLORS[c] || '#888'} connectNulls dot={{ r: 3 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            <div className="flex justify-end">
              <Dialog open={showEvalDialog} onOpenChange={setShowEvalDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-evaluation-btn"><Plus className="h-4 w-4 mr-2" />New Evaluation</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Record Evaluation</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateEvaluation} className="space-y-4" data-testid="evaluation-form">
                    <div className="flex items-center justify-between p-2 bg-muted/40 rounded-md">
                      <p className="text-xs text-muted-foreground">Speak the evaluation and I'll try to fill it in</p>
                      <VoiceInput onTranscript={handleEvaluationVoiceTranscript} placeholder="Speak evaluation" size="icon" variant="outline" data-testid="evaluation-voice-btn" />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={evalForm.category} onValueChange={(v) => setEvalForm({ ...evalForm, category: v })}>
                        <SelectTrigger data-testid="evaluation-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Rating (1-10)</Label>
                      <Input type="number" min="1" max="10" required value={evalForm.rating} onChange={(e) => setEvalForm({ ...evalForm, rating: e.target.value })} data-testid="evaluation-rating-input" />
                    </div>
                    <div>
                      <Label>Tags (comma-separated)</Label>
                      <Input value={evalForm.tags} onChange={(e) => setEvalForm({ ...evalForm, tags: e.target.value })} placeholder="footwork, effort" data-testid="evaluation-tags-input" />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea value={evalForm.notes} onChange={(e) => setEvalForm({ ...evalForm, notes: e.target.value })} data-testid="evaluation-notes-input" />
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-evaluation-btn">Save Evaluation</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {evaluations.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">No evaluations yet.</CardContent></Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {evaluations.map((ev) => (
                  <Card key={ev.id} data-testid={`evaluation-card-${ev.id}`} className={ev.retracted ? 'opacity-50' : ''}>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">{ev.category}</Badge>
                        <Badge>{ev.rating}/10</Badge>
                      </div>
                      {ev.tags?.length > 0 && (
                        <div className="flex gap-1 flex-wrap">{ev.tags.map((t, i) => <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>)}</div>
                      )}
                      {ev.notes && <p className="text-sm text-muted-foreground">{ev.notes}</p>}
                      <p className="text-xs text-muted-foreground">{new Date(ev.evaluated_at).toLocaleDateString()}</p>
                      {ev.retracted ? (
                        <Badge variant="destructive" className="text-xs">Retracted</Badge>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => handleUndoEvaluation(ev.id)} data-testid={`undo-evaluation-${ev.id}`}>
                          <Undo2 className="h-4 w-4 mr-1" />Undo
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="interventions" className="space-y-4 mt-4">
            <div className="flex justify-end">
              <Dialog open={showInterventionDialog} onOpenChange={setShowInterventionDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-intervention-btn"><Plus className="h-4 w-4 mr-2" />New Intervention</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Log Intervention</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateIntervention} className="space-y-4" data-testid="intervention-form">
                    <div>
                      <Label>Category (optional)</Label>
                      <Select value={interventionForm.category || 'none'} onValueChange={(v) => setInterventionForm({ ...interventionForm, category: v === 'none' ? '' : v })}>
                        <SelectTrigger data-testid="intervention-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input required value={interventionForm.title} onChange={(e) => setInterventionForm({ ...interventionForm, title: e.target.value })} data-testid="intervention-title-input" />
                    </div>
                    <div>
                      <Label>Link to goal (optional)</Label>
                      <Select value={interventionForm.goal_id || 'none'} onValueChange={(v) => setInterventionForm({ ...interventionForm, goal_id: v === 'none' ? '' : v })}>
                        <SelectTrigger data-testid="intervention-goal-select"><SelectValue placeholder="No linked goal" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No linked goal</SelectItem>
                          {goals.map((g) => (
                            <SelectItem key={g.id} value={g.id}>
                              {g.title} {g.status !== 'open' ? `(${g.status})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={interventionForm.description} onChange={(e) => setInterventionForm({ ...interventionForm, description: e.target.value })} data-testid="intervention-description-input" />
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-intervention-btn">Log Intervention</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {interventions.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">No interventions logged yet.</CardContent></Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {interventions.map((iv) => {
                  const linkedGoal = iv.goal_id ? goals.find((g) => g.id === iv.goal_id) : null;
                  return (
                  <Card key={iv.id} data-testid={`intervention-card-${iv.id}`} className={iv.retracted ? 'opacity-50' : ''}>
                    <CardContent className="p-4 space-y-2">
                      {iv.category && <Badge variant="outline">{iv.category}</Badge>}
                      <p className="font-semibold">{iv.title}</p>
                      {iv.description && <p className="text-sm text-muted-foreground">{iv.description}</p>}
                      {linkedGoal && (
                        <p className="text-xs flex items-center gap-1 text-primary" data-testid={`intervention-goal-link-${iv.id}`}>
                          <Target className="h-3 w-3" /> Linked to goal: {linkedGoal.title}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">{new Date(iv.created_at).toLocaleDateString()}</p>
                      {iv.retracted ? (
                        <Badge variant="destructive" className="text-xs">Retracted</Badge>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => handleUndoIntervention(iv.id)} data-testid={`undo-intervention-${iv.id}`}>
                          <Undo2 className="h-4 w-4 mr-1" />Undo
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="profile" className="space-y-4 mt-4">
            {PROFILE_CATEGORIES.map((c) => {
              const key = c.toLowerCase();
              return (
                <Card key={c}>
                  <CardHeader><CardTitle className="text-lg">{c} Notes</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <Textarea
                      value={profileDrafts[key] || ''}
                      onChange={(e) => setProfileDrafts({ ...profileDrafts, [key]: e.target.value })}
                      rows={4}
                      data-testid={`profile-summary-${key}`}
                    />
                    <Button size="sm" onClick={() => handleSaveProfile(c)} data-testid={`save-profile-${key}-btn`}>Save Notes</Button>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
